import { useEffect, useMemo, useRef, useState } from "react";
import axios from "axios";
import { motion, AnimatePresence } from "framer-motion";
import {
  format,
  addMonths,
  subMonths,
  startOfMonth,
  endOfMonth,
  startOfWeek,
  endOfWeek,
  eachDayOfInterval,
  isSameMonth,
  isSameDay,
  isBefore,
  startOfDay,
} from "date-fns";
import { fr } from "date-fns/locale";
import {
  ChevronLeft,
  ChevronRight,
  ArrowRight,
  ArrowLeft,
  CheckCircle2,
  Plane,
  Clock,
  Lock,
  Trophy,
  Award,
  MapPin,
  Sparkles,
} from "lucide-react";
import { toast } from "sonner";

import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Formulas dynamically depend on selected tarif (classique or licencie)
const FORMULAS_BY_TARIF = {
  classique: [
    { value: "individuelle_classique", label: "Programme 1 — Séances individuelles à la carte" },
    { value: "u13_senior_classique", label: "Programme 2 — U13 / Senior (4 séances · 250€)" },
    { value: "n3_pro", label: "Programme 3 — N3 / PRO (sur devis)" },
    { value: "club", label: "Collaboration Club" },
  ],
  licencie: [
    { value: "individuelle_licencie", label: "Programme 1 — Séances individuelles à la carte (−30%)" },
    { value: "u13_senior_licencie", label: "Programme 2 — U13 / Senior (4 séances · 175€)" },
  ],
};

const TARIF_LABELS = {
  classique: "Tarif Classique",
  licencie: "Tarif Licencié",
};

const AGES = ["U9 - U12", "U13 - U15", "U16 - U17", "U18 - U19", "Senior"];
const LEVELS = ["Débutant", "Amateur", "Régional", "National", "Pro / Semi-pro"];

const STEP_LABELS = ["Date & créneau", "Tes informations", "Confirmation"];

const COACHES = [
  {
    id: "jeremy_aymes",
    name: "Jérémy Aymes",
    firstName: "JÉRÉMY",
    lastName: "AYMES",
    role: "",
    available: true,
    image:
      "https://customer-assets.emergentagent.com/job_football-training-37/artifacts/8mse631h_jrremy%20aymes%20final%20agenda.png",
    imageScale: 0.62,
    imageOffsetY: "28%",
    imageOriginY: "top",
    profileScale: 1.0,
    profileOffsetY: "0%",
    profileOriginY: "top",
    location: "Martigues (13)",
    overall: "92",
    stats: [
      { label: "Ans Pro", value: "15+" },
      { label: "Matchs", value: "250+" },
      { label: "Niveau", value: "L2" },
    ],
    bio:
      "Né le 12 juillet 1988 à Martigues, gardien de but professionnel pendant plus de 15 ans en National et en Ligue 2. Titulaire du BEF (Brevet d'Entraîneur de Football) et du diplôme fédéral FFF. Fondateur d'une micro-entreprise spécialisée dans l'entraînement particulier de gardiens de but. En 2014, création de l'École des Gardiens de But à l'US Granville — engagement fort envers la transmission et le développement des jeunes talents. La passion pour le poste de gardien et le désir d'accompagner la nouvelle génération sont au cœur de l'approche pédagogique.",
    timeline: [
      {
        num: "01",
        club: "Formation à Martigues",
        detail: "Né et formé dans sa ville natale (Bouches-du-Rhône).",
      },
      {
        num: "02",
        club: "Centre de Formation — Olympique Lyonnais",
        detail: "4 saisons au centre de formation de l'OL.",
      },
      {
        num: "03",
        club: "US Granville",
        detail:
          "National 2 — Plusieurs saisons. Création de l'École des Gardiens de But en 2014.",
      },
      {
        num: "04",
        club: "Le Mans FC",
        detail:
          "National puis Ligue 2 — 3 saisons (2018-2021). 28 matchs en 2018-2019, contribution à la montée en Ligue 2.",
      },
      {
        num: "05",
        club: "FC Martigues",
        detail:
          "National 2, National et Ligue 2 — 74 matchs disputés. 2 montées historiques : National 2 → National → Ligue 2.",
      },
      {
        num: "06",
        club: "AS Cannes",
        detail:
          "National 2 — Arrivée en novembre 2024. 20 matchs disputés en saison 2024-2025.",
      },
      {
        num: "07",
        club: "Académie GB Martigues",
        detail: "Fondateur et entraîneur de l'Académie Gardiens de But de Martigues.",
      },
    ],
  },
  {
    id: "yves_aymes",
    name: "Yves Aymes",
    firstName: "YVES",
    lastName: "AYMES",
    role: "",
    available: true,
    image:
      "https://customer-assets.emergentagent.com/job_football-training-37/artifacts/qbqexdwm_gardien%202%20jeremy%20final%20.png",
    imageScale: 0.88,
    imageOffsetY: "-3%",
    imageOriginY: "top",
    profileScale: 1.3,
    profileOffsetY: "-22%",
    profileOriginY: "top",
    location: "Martigues (13)",
    overall: "88",
    stats: [
      { label: "Ans Coaching", value: "10+" },
      { label: "Catégories", value: "U10-U16" },
      { label: "Spécialité", value: "Jeunes" },
    ],
    bio:
      "Entraîneur de gardiens à l'École des Gardiens du FCIOP de la saison 2008/2009 à la saison 2018/2019, soit plus de 10 saisons consécutives consacrées exclusivement à la formation et au développement des jeunes gardiens. Pédagogie particulièrement adaptée aux catégories U10 à U16 : approche progressive, exercices ludiques et structurés, suivi attentif de chaque profil. Une expérience reconnue dans l'enseignement, l'accompagnement et la transmission auprès des jeunes générations — un véritable atout pour les gardiens en construction.",
    timeline: [
      {
        num: "01",
        club: "École des Gardiens du FCIOP",
        detail:
          "Arrivée saison 2008/2009 — Début de la mission d'entraîneur de gardiens dédié aux jeunes.",
      },
      {
        num: "02",
        club: "Spécialisation Pédagogie Jeunes",
        detail:
          "Approche taillée pour les catégories U10 à U16 : technique, motricité, lecture du jeu et confiance.",
      },
      {
        num: "03",
        club: "10 saisons consécutives au FCIOP",
        detail:
          "Plus d'une décennie au service de la formation des gardiens du club, de nombreux profils accompagnés et développés.",
      },
      {
        num: "04",
        club: "Fin de mandat FCIOP",
        detail: "Saison 2018/2019 — Clôture du cycle au sein de l'École des Gardiens.",
      },
      {
        num: "05",
        club: "AYMES Goalkeeper Coaching",
        detail:
          "Coach Senior dédié aux jeunes gardiens — Mise à disposition de l'expérience auprès des U10 à U16.",
      },
    ],
  },
];

export default function Booking() {
  const today = useMemo(() => startOfDay(new Date()), []);
  // phase: "tarif-select" | "coach-select" | "coach-detail" | "schedule" | "info" | "success"
  const [phase, setPhase] = useState("tarif-select");
  const [selectedTarif, setSelectedTarif] = useState(null);
  const [selectedCoach, setSelectedCoach] = useState(null);

  // Schedule state
  const [viewMonth, setViewMonth] = useState(startOfMonth(today));
  const [monthData, setMonthData] = useState({ open_dates: [], full_dates: [] });
  const [loadingMonth, setLoadingMonth] = useState(false);
  const [selectedDate, setSelectedDate] = useState(null);
  const [selectedSlot, setSelectedSlot] = useState(null);
  const [daySlots, setDaySlots] = useState([]);
  const [loadingSlots, setLoadingSlots] = useState(false);

  // Form state
  const [formula, setFormula] = useState("individuelle_classique");
  const [fullName, setFullName] = useState("");
  const [email, setEmail] = useState("");
  const [phone, setPhone] = useState("");
  const [ageCategory, setAgeCategory] = useState("");
  const [level, setLevel] = useState("");
  const [message, setMessage] = useState("");
  const [submitting, setSubmitting] = useState(false);

  // Fetch month availability when entering schedule phase or changing month
  useEffect(() => {
    if (phase !== "schedule") return;
    let cancel = false;
    (async () => {
      setLoadingMonth(true);
      try {
        const y = viewMonth.getFullYear();
        const m = viewMonth.getMonth() + 1;
        const { data } = await axios.get(`${API}/availability/month/${y}/${m}`);
        if (!cancel) setMonthData(data);
      } catch {
        if (!cancel) setMonthData({ open_dates: [], full_dates: [] });
      } finally {
        if (!cancel) setLoadingMonth(false);
      }
    })();
    return () => {
      cancel = true;
    };
  }, [viewMonth, phase]);

  // Fetch slots when date selected
  useEffect(() => {
    if (!selectedDate) return undefined;
    let cancel = false;
    (async () => {
      setLoadingSlots(true);
      try {
        const ds = format(selectedDate, "yyyy-MM-dd");
        const { data } = await axios.get(`${API}/availability/day/${ds}`);
        if (!cancel) setDaySlots(data.slots || []);
      } catch {
        if (!cancel) setDaySlots([]);
      } finally {
        if (!cancel) setLoadingSlots(false);
      }
    })();
    return () => {
      cancel = true;
    };
  }, [selectedDate]);

  const calendarDays = useMemo(() => {
    const start = startOfWeek(startOfMonth(viewMonth), { weekStartsOn: 1 });
    const end = endOfWeek(endOfMonth(viewMonth), { weekStartsOn: 1 });
    return eachDayOfInterval({ start, end });
  }, [viewMonth]);

  // Auto-scroll to the booking section on every phase change (skip initial mount)
  const isFirstPhaseRef = useRef(true);
  useEffect(() => {
    if (isFirstPhaseRef.current) {
      isFirstPhaseRef.current = false;
      return;
    }
    const el = document.getElementById("booking");
    if (el) {
      el.scrollIntoView({ behavior: "smooth", block: "start" });
    }
  }, [phase]);

  const openSet = useMemo(() => new Set(monthData.open_dates || []), [monthData]);

  const goPrev = () => setViewMonth(subMonths(viewMonth, 1));
  const goNext = () => setViewMonth(addMonths(viewMonth, 1));
  const canGoPrev = !isBefore(subMonths(viewMonth, 1), startOfMonth(today));

  const onDayClick = (d) => {
    if (isBefore(d, today)) return;
    const ds = format(d, "yyyy-MM-dd");
    if (!openSet.has(ds)) return;
    setSelectedDate(d);
    setSelectedSlot(null);
  };

  const pickTarif = (tarif) => {
    setSelectedTarif(tarif);
    // Default formula based on tarif
    setFormula(
      tarif === "classique" ? "individuelle_classique" : "individuelle_licencie"
    );
    setPhase("coach-select");
  };

  const pickCoach = (coach) => {
    if (!coach.available) return;
    setSelectedCoach(coach);
    setPhase("coach-detail");
  };

  const startBooking = () => setPhase("schedule");

  const goToInfo = () => {
    if (!selectedDate || !selectedSlot) {
      toast.error("Choisis une date et un créneau pour continuer.");
      return;
    }
    setPhase("info");
  };

  const submit = async () => {
    if (!fullName || !email || !phone) {
      toast.error("Nom, email et téléphone sont obligatoires.");
      return;
    }
    setSubmitting(true);
    try {
      await axios.post(`${API}/bookings`, {
        full_name: fullName,
        email,
        phone,
        formula,
        tarif: selectedTarif,
        preferred_date: format(selectedDate, "yyyy-MM-dd"),
        slot_time: selectedSlot,
        coach: selectedCoach.id,
        age_category: ageCategory || null,
        level: level || null,
        message: message || "",
      });
      setPhase("success");
    } catch (err) {
      const detail = err?.response?.data?.detail;
      if (err?.response?.status === 409) {
        toast.error("Ce créneau vient d'être réservé. Choisis-en un autre.");
        setPhase("schedule");
        setSelectedSlot(null);
        setSelectedDate((d) => (d ? new Date(d) : null));
      } else {
        toast.error(typeof detail === "string" ? detail : "Erreur lors de l'envoi.");
      }
    } finally {
      setSubmitting(false);
    }
  };

  const resetAll = () => {
    setPhase("tarif-select");
    setSelectedTarif(null);
    setSelectedCoach(null);
    setSelectedDate(null);
    setSelectedSlot(null);
    setFullName("");
    setEmail("");
    setPhone("");
    setAgeCategory("");
    setLevel("");
    setMessage("");
  };

  const weekHeaders = ["Lun", "Mar", "Mer", "Jeu", "Ven", "Sam", "Dim"];
  const isInBookingFlow =
    phase === "schedule" || phase === "info" || phase === "success";

  return (
    <section
      id="booking"
      data-testid="booking-section"
      className="relative bg-[#050505] py-24 md:py-32 px-6 md:px-12 border-t border-white/5 overflow-hidden"
    >
      <div className="max-w-7xl mx-auto">
        {/* Title — adapts to phase */}
        <div className="mb-12">
          <AnimatePresence mode="wait">
            {phase === "tarif-select" && (
              <motion.div
                key="t-tarif"
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -10 }}
                className="grid grid-cols-1 lg:grid-cols-12 gap-10 items-end"
              >
                <h2 className="lg:col-span-7 font-display text-5xl sm:text-6xl lg:text-7xl leading-[0.9]">
                  RÉSERVE
                  <br />
                  <span className="text-[#CCFF00]">TA SÉANCE.</span>
                </h2>
                <p className="lg:col-span-5 text-zinc-400 text-base md:text-lg leading-relaxed">
                  Tu es licencié au sein de la structure ? Profite du tarif
                  préférentiel −30%. Sinon, sélectionne le tarif classique.
                </p>
              </motion.div>
            )}
            {phase === "coach-select" && (
              <motion.div
                key="t-select"
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -10 }}
              >
                <div className="flex items-center justify-between gap-6 mb-6">
                  <button
                    onClick={() => {
                      setSelectedTarif(null);
                      setPhase("tarif-select");
                    }}
                    data-testid="back-to-tarif-select"
                    className="inline-flex items-center gap-2 text-xs uppercase tracking-[0.25em] text-zinc-400 hover:text-[#CCFF00] transition-colors"
                  >
                    <ArrowLeft className="w-3 h-3" /> Changer de tarif
                  </button>
                  <p className="text-[10px] uppercase tracking-[0.3em] text-[#CCFF00]">
                    {selectedTarif && TARIF_LABELS[selectedTarif]}
                  </p>
                </div>
                <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 items-end">
                  <h2 className="lg:col-span-7 font-display text-5xl sm:text-6xl lg:text-7xl leading-[0.9]">
                    CHOISIS
                    <br />
                    <span className="text-[#CCFF00]">TON COACH.</span>
                  </h2>
                  <p className="lg:col-span-5 text-zinc-400 text-base md:text-lg leading-relaxed">
                    Sélectionne le coach avec qui tu souhaites travailler.
                    Découvre son parcours, puis réserve directement ton créneau.
                  </p>
                </div>
              </motion.div>
            )}
            {phase === "coach-detail" && (
              <motion.div
                key="t-detail"
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -10 }}
                className="flex items-center justify-between gap-6"
              >
                <button
                  onClick={() => {
                    setSelectedCoach(null);
                    setPhase("coach-select");
                  }}
                  data-testid="back-to-coach-select"
                  className="inline-flex items-center gap-2 text-xs uppercase tracking-[0.25em] text-zinc-400 hover:text-[#CCFF00] transition-colors"
                >
                  <ArrowLeft className="w-3 h-3" /> Changer de coach
                </button>
                <p className="text-[10px] uppercase tracking-[0.3em] text-zinc-600">
                  Profil coach
                </p>
              </motion.div>
            )}
            {isInBookingFlow && (
              <motion.div
                key="t-book"
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -10 }}
              >
                <div className="flex items-center justify-between gap-6 mb-8">
                  <button
                    onClick={() => setPhase("coach-detail")}
                    data-testid="back-to-coach-detail"
                    className="inline-flex items-center gap-2 text-xs uppercase tracking-[0.25em] text-zinc-400 hover:text-[#CCFF00] transition-colors"
                  >
                    <ArrowLeft className="w-3 h-3" /> Coach : {selectedCoach?.firstName} {selectedCoach?.lastName}
                  </button>
                </div>
                <h2 className="font-display text-5xl sm:text-6xl lg:text-7xl leading-[0.9]">
                  RÉSERVE
                  <br />
                  <span className="text-[#CCFF00]">TON CRÉNEAU.</span>
                </h2>
              </motion.div>
            )}
          </AnimatePresence>
        </div>

        {/* ============ PHASE: TARIF SELECT ============ */}
        <AnimatePresence mode="wait">
          {phase === "tarif-select" && (
            <motion.div
              key="phase-tarif"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              transition={{ duration: 0.4 }}
              className="grid grid-cols-1 md:grid-cols-2 gap-6"
              data-testid="tarif-select-grid"
            >
              <TarifCard
                id="classique"
                title="Tarif Classique"
                badge="Public"
                description="Tarifs publics pour gardiens non-licenciés au sein de la structure."
                bullets={[
                  "1h : 50 €",
                  "5 séances : 200 €",
                  "10 séances : 400 €",
                  "Programme U13 — Senior : 250 €",
                  "Programme N3 / PRO sur devis",
                ]}
                onPick={pickTarif}
                accent={false}
              />
              <TarifCard
                id="licencie"
                title="Tarif Licencié"
                badge="−30%"
                description="Tarif préférentiel pour les gardiens licenciés au sein de la structure."
                bullets={[
                  "1h : 35 € (au lieu de 50 €)",
                  "5 séances : 140 € (au lieu de 200 €)",
                  "10 séances : 280 € (au lieu de 400 €)",
                  "Programme U13 — Senior : 175 € (au lieu de 250 €)",
                ]}
                onPick={pickTarif}
                accent
              />
            </motion.div>
          )}

          {/* ============ PHASE: COACH SELECT ============ */}
          {phase === "coach-select" && (
            <motion.div
              key="phase-select"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              transition={{ duration: 0.4 }}
              className="grid grid-cols-1 md:grid-cols-2 gap-6"
              data-testid="coach-select-grid"
            >
              {COACHES.map((c, i) => (
                <CoachCard key={c.id} coach={c} index={i} onPick={pickCoach} />
              ))}
            </motion.div>
          )}

          {/* ============ PHASE: COACH DETAIL ============ */}
          {phase === "coach-detail" && selectedCoach && (
            <motion.div
              key="phase-detail"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              transition={{ duration: 0.5 }}
              className="grid grid-cols-1 lg:grid-cols-12 gap-6 lg:gap-10"
              data-testid="coach-detail"
            >
              {/* Coach portrait */}
              <motion.div
                initial={{ x: -60, opacity: 0 }}
                animate={{ x: 0, opacity: 1 }}
                transition={{ duration: 0.6, ease: "easeOut" }}
                className="lg:col-span-6 relative bg-gradient-to-br from-[#0a0a0a] via-[#0a0a0a] to-[#050505] border border-white/10 rounded-sm overflow-hidden min-h-[560px] lg:min-h-[840px] lg:sticky lg:top-24 lg:self-start"
              >
                <CoachBackdrop />
                <div className="absolute top-6 left-6 z-10 flex items-center gap-3">
                  {selectedCoach.role && (
                    <div className="bg-black/60 backdrop-blur-sm border border-white/10 px-3 py-1.5">
                      <p className="text-[10px] uppercase tracking-[0.2em] text-zinc-300">
                        {selectedCoach.role}
                      </p>
                    </div>
                  )}
                </div>
                <div className="absolute bottom-0 left-0 right-0 p-6 md:p-8 z-10 bg-gradient-to-t from-black via-black/80 to-transparent">
                  <p className="font-display text-6xl md:text-7xl lg:text-8xl leading-[0.9]">
                    {selectedCoach.firstName}
                    <br />
                    <span className="text-[#CCFF00]">{selectedCoach.lastName}</span>
                  </p>
                </div>
                <img
                  src={selectedCoach.image}
                  alt={selectedCoach.name}
                  style={{
                    transform: `translateY(${selectedCoach.profileOffsetY || "0%"}) scale(${selectedCoach.profileScale || 1})`,
                    transformOrigin: `center ${selectedCoach.profileOriginY || "top"}`,
                  }}
                  className="absolute inset-0 w-full h-full object-cover object-top drop-shadow-[0_30px_40px_rgba(0,0,0,0.7)] z-[1]"
                />
              </motion.div>

              {/* Bio + timeline + CTA */}
              <motion.div
                initial={{ x: 60, opacity: 0 }}
                animate={{ x: 0, opacity: 1 }}
                transition={{ duration: 0.6, delay: 0.1, ease: "easeOut" }}
                className="lg:col-span-6 flex flex-col gap-6"
              >
                {/* Stats row */}
                <div className="grid grid-cols-3 gap-px bg-white/10 border border-white/10">
                  {selectedCoach.stats.map((s) => (
                    <div key={s.label} className="bg-[#0a0a0a] p-5 text-center">
                      <p className="font-display text-3xl md:text-4xl text-[#CCFF00]">
                        {s.value}
                      </p>
                      <p className="mt-1 text-[10px] uppercase tracking-[0.25em] text-zinc-500">
                        {s.label}
                      </p>
                    </div>
                  ))}
                </div>

                {/* Bio */}
                <div className="bg-[#0a0a0a] border border-white/10 p-6 md:p-8 rounded-sm">
                  <p className="text-[10px] uppercase tracking-[0.25em] text-[#CCFF00] mb-3 flex items-center gap-2">
                    <Award className="w-3 h-3" /> Profil
                  </p>
                  <p className="text-zinc-300 text-sm md:text-base leading-relaxed">
                    {selectedCoach.bio}
                  </p>
                </div>

                {/* Timeline */}
                <div className="bg-[#0a0a0a] border border-white/10 p-6 md:p-8 rounded-sm">
                  <p className="text-[10px] uppercase tracking-[0.25em] text-[#CCFF00] mb-5 flex items-center gap-2">
                    <Trophy className="w-3 h-3" /> Carrière de joueur
                  </p>
                  <ul className="space-y-3">
                    {selectedCoach.timeline.map((t) => (
                      <li key={t.num} className="flex gap-4">
                        <span className="font-display text-xl text-[#CCFF00] w-6 flex-shrink-0">
                          {t.num}
                        </span>
                        <div className="flex-1 border-l border-white/10 pl-4 pb-1">
                          <p className="font-bold text-white text-sm uppercase tracking-wider">
                            {t.club}
                          </p>
                          <p className="text-xs text-zinc-500 mt-1">{t.detail}</p>
                        </div>
                      </li>
                    ))}
                  </ul>
                </div>

                {/* CTA */}
                <button
                  onClick={startBooking}
                  data-testid="cta-reserve-creneau"
                  className="group inline-flex items-center justify-center gap-3 bg-[#CCFF00] text-black font-bold uppercase tracking-wide text-sm md:text-base px-8 py-5 rounded-sm hover:bg-white transition-colors"
                >
                  Réserve ton créneau
                  <ArrowRight className="w-5 h-5 group-hover:translate-x-1 transition-transform" />
                </button>
              </motion.div>
            </motion.div>
          )}

          {/* ============ PHASE: SCHEDULE ============ */}
          {phase === "schedule" && (
            <motion.div
              key="phase-schedule"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0 }}
              transition={{ duration: 0.4 }}
            >
              <Stepper current={0} />
              <div className="grid grid-cols-1 lg:grid-cols-12 gap-6" data-testid="booking-step-0">
                {/* Calendar */}
                <div className="lg:col-span-7 bg-[#0a0a0a] border border-white/10 p-6 md:p-8 rounded-sm">
                  <div className="flex items-center justify-between mb-6">
                    <button
                      type="button"
                      onClick={goPrev}
                      disabled={!canGoPrev}
                      data-testid="cal-prev"
                      className="w-10 h-10 grid place-items-center border border-white/10 hover:border-[#CCFF00]/50 disabled:opacity-30 disabled:cursor-not-allowed transition-colors"
                      aria-label="Mois précédent"
                    >
                      <ChevronLeft className="w-4 h-4" />
                    </button>
                    <p className="font-display text-3xl md:text-4xl tracking-wide" data-testid="cal-month-label">
                      {format(viewMonth, "MMMM yyyy", { locale: fr })}
                    </p>
                    <button
                      type="button"
                      onClick={goNext}
                      data-testid="cal-next"
                      className="w-10 h-10 grid place-items-center border border-white/10 hover:border-[#CCFF00]/50 transition-colors"
                      aria-label="Mois suivant"
                    >
                      <ChevronRight className="w-4 h-4" />
                    </button>
                  </div>

                  <div className="grid grid-cols-7 gap-1 mb-2">
                    {weekHeaders.map((w) => (
                      <div
                        key={w}
                        className="text-[10px] uppercase tracking-[0.2em] text-zinc-600 text-center py-2"
                      >
                        {w}
                      </div>
                    ))}
                  </div>

                  <div className="grid grid-cols-7 gap-1 relative">
                    {loadingMonth && (
                      <div className="absolute inset-0 bg-[#0a0a0a]/60 backdrop-blur-sm grid place-items-center z-10">
                        <span className="text-xs text-zinc-500 uppercase tracking-widest">
                          Chargement…
                        </span>
                      </div>
                    )}
                    {calendarDays.map((d) => {
                      const inMonth = isSameMonth(d, viewMonth);
                      const ds = format(d, "yyyy-MM-dd");
                      const isOpen = openSet.has(ds);
                      const isPast = isBefore(d, today);
                      const isSelected = selectedDate && isSameDay(d, selectedDate);
                      const isTodayDay = isSameDay(d, today);
                      const clickable = inMonth && isOpen && !isPast;

                      return (
                        <button
                          key={ds}
                          type="button"
                          disabled={!clickable}
                          onClick={() => onDayClick(d)}
                          data-testid={`cal-day-${ds}`}
                          className={`relative aspect-square flex flex-col items-center justify-center border text-sm transition-all ${
                            isSelected
                              ? "bg-[#CCFF00] text-black border-[#CCFF00] font-bold"
                              : clickable
                              ? "border-white/10 text-white hover:border-[#CCFF00]/60 hover:bg-[#CCFF00]/5"
                              : "border-transparent text-zinc-700 cursor-not-allowed"
                          } ${!inMonth ? "opacity-30" : ""}`}
                        >
                          <span className={isTodayDay && !isSelected ? "underline decoration-[#CCFF00] decoration-2 underline-offset-4" : ""}>
                            {format(d, "d")}
                          </span>
                          {clickable && !isSelected && (
                            <span className="absolute bottom-1 w-1 h-1 rounded-full bg-[#CCFF00]" />
                          )}
                        </button>
                      );
                    })}
                  </div>

                  <div className="mt-6 flex flex-wrap gap-5 text-[10px] uppercase tracking-[0.2em] text-zinc-500">
                    <span className="flex items-center gap-2">
                      <span className="w-1.5 h-1.5 rounded-full bg-[#CCFF00]" /> Disponible
                    </span>
                    <span className="flex items-center gap-2">
                      <span className="w-3 h-3 bg-[#CCFF00]" /> Sélectionné
                    </span>
                    <span className="flex items-center gap-2">
                      <span className="w-3 h-3 border border-white/10" /> Indisponible
                    </span>
                  </div>
                </div>

                {/* Slot picker */}
                <div className="lg:col-span-5 bg-[#0a0a0a] border border-white/10 p-6 md:p-8 rounded-sm flex flex-col">
                  <div className="flex items-center gap-3 text-[10px] uppercase tracking-[0.25em] text-zinc-500 mb-4">
                    <Clock className="w-3.5 h-3.5 text-[#CCFF00]" /> Créneaux du jour
                  </div>

                  {!selectedDate ? (
                    <div className="flex-1 grid place-items-center text-center py-12">
                      <div>
                        <Plane className="w-10 h-10 text-zinc-700 mx-auto mb-4" strokeWidth={1.2} />
                        <p className="font-display text-2xl text-zinc-500">
                          SÉLECTIONNE UNE DATE
                        </p>
                        <p className="text-xs text-zinc-600 mt-2 max-w-xs">
                          Pick une date dans l&apos;agenda pour voir les créneaux libres.
                        </p>
                      </div>
                    </div>
                  ) : (
                    <>
                      <p className="font-display text-2xl mb-1">
                        {format(selectedDate, "EEEE d MMMM", { locale: fr }).toUpperCase()}
                      </p>
                      <p className="text-xs text-zinc-500 uppercase tracking-widest mb-6">
                        Séances de 1h
                      </p>

                      {loadingSlots ? (
                        <p className="text-zinc-500 text-sm">Chargement des créneaux…</p>
                      ) : daySlots.length === 0 ? (
                        <p className="text-zinc-500 text-sm">Aucun créneau ce jour-là.</p>
                      ) : (
                        <div className="grid grid-cols-3 gap-2 flex-1" data-testid="slot-grid">
                          {daySlots.map((s) => {
                            const isSel = selectedSlot === s.time;
                            return (
                              <button
                                type="button"
                                key={s.time}
                                disabled={!s.available}
                                onClick={() => setSelectedSlot(s.time)}
                                data-testid={`slot-${s.time}`}
                                className={`py-3 text-sm font-bold tracking-wider border transition-colors ${
                                  isSel
                                    ? "bg-[#CCFF00] text-black border-[#CCFF00]"
                                    : s.available
                                    ? "border-white/10 text-white hover:border-[#CCFF00]/60"
                                    : "border-white/5 text-zinc-700 line-through cursor-not-allowed"
                                }`}
                              >
                                {s.time}
                              </button>
                            );
                          })}
                        </div>
                      )}

                      <button
                        type="button"
                        onClick={goToInfo}
                        disabled={!selectedSlot}
                        data-testid="step-0-continue"
                        className="mt-6 inline-flex items-center justify-center gap-2 bg-[#CCFF00] text-black font-bold uppercase tracking-wide text-sm py-4 rounded-sm hover:bg-white transition-colors disabled:opacity-30 disabled:cursor-not-allowed"
                      >
                        Continuer <ArrowRight className="w-4 h-4" />
                      </button>
                    </>
                  )}
                </div>
              </div>
            </motion.div>
          )}

          {/* ============ PHASE: INFO ============ */}
          {phase === "info" && (
            <motion.div
              key="phase-info"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0 }}
              transition={{ duration: 0.4 }}
            >
              <Stepper current={1} />
              <div className="grid grid-cols-1 lg:grid-cols-12 gap-6" data-testid="booking-step-1">
                <aside className="lg:col-span-4 bg-[#0a0a0a] border border-white/10 p-6 md:p-8 rounded-sm h-fit">
                  <p className="text-[10px] uppercase tracking-[0.25em] text-[#CCFF00] mb-4">
                    Récap réservation
                  </p>
                  <p className="text-[10px] uppercase tracking-[0.2em] text-zinc-500 mb-1">
                    Tarif
                  </p>
                  <p className="font-display text-xl mb-3">{TARIF_LABELS[selectedTarif]}</p>
                  <p className="text-[10px] uppercase tracking-[0.2em] text-zinc-500 mb-1">
                    Coach
                  </p>
                  <p className="font-display text-2xl mb-4">{selectedCoach?.firstName} {selectedCoach?.lastName}</p>
                  <p className="font-display text-3xl leading-none">
                    {format(selectedDate, "d MMM yyyy", { locale: fr }).toUpperCase()}
                  </p>
                  <p className="font-display text-5xl text-[#CCFF00] mt-2 leading-none">
                    {selectedSlot}
                  </p>
                  <div className="mt-6 pt-6 border-t border-white/10 space-y-3 text-sm text-zinc-400">
                    <div className="flex justify-between">
                      <span className="text-zinc-600 uppercase text-[10px] tracking-widest">Durée</span>
                      <span className="text-white">1h</span>
                    </div>
                  </div>
                  <button
                    type="button"
                    onClick={() => setPhase("schedule")}
                    data-testid="step-1-back"
                    className="mt-8 inline-flex items-center gap-2 text-xs uppercase tracking-widest text-zinc-400 hover:text-[#CCFF00]"
                  >
                    <ArrowLeft className="w-3 h-3" /> Modifier
                  </button>
                </aside>

                <form
                  onSubmit={(e) => {
                    e.preventDefault();
                    submit();
                  }}
                  className="lg:col-span-8 bg-[#0a0a0a] border border-white/10 p-6 md:p-10 rounded-sm grid grid-cols-1 md:grid-cols-2 gap-5"
                  data-testid="booking-form"
                >
                  <div className="md:col-span-2">
                    <Label className="text-[10px] uppercase tracking-[0.2em] text-zinc-500">Formule *</Label>
                    <Select value={formula} onValueChange={setFormula}>
                      <SelectTrigger className="mt-2 bg-black/50 border-white/10 text-white h-12 rounded-sm focus:ring-[#CCFF00]" data-testid="booking-formula-select">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent className="bg-[#0a0a0a] border-white/10 text-white">
                        {(FORMULAS_BY_TARIF[selectedTarif] || []).map((f) => (
                          <SelectItem key={f.value} value={f.value}>{f.label}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <Label className="text-[10px] uppercase tracking-[0.2em] text-zinc-500">Nom complet *</Label>
                    <Input value={fullName} onChange={(e) => setFullName(e.target.value)} placeholder="Prénom Nom" required data-testid="booking-name-input" className="mt-2 bg-black/50 border-white/10 text-white placeholder:text-zinc-600 h-12 rounded-sm focus-visible:ring-[#CCFF00]" />
                  </div>
                  <div>
                    <Label className="text-[10px] uppercase tracking-[0.2em] text-zinc-500">Email *</Label>
                    <Input type="email" value={email} onChange={(e) => setEmail(e.target.value)} placeholder="ton@email.com" required data-testid="booking-email-input" className="mt-2 bg-black/50 border-white/10 text-white placeholder:text-zinc-600 h-12 rounded-sm focus-visible:ring-[#CCFF00]" />
                  </div>
                  <div>
                    <Label className="text-[10px] uppercase tracking-[0.2em] text-zinc-500">Téléphone *</Label>
                    <Input value={phone} onChange={(e) => setPhone(e.target.value)} placeholder="06 12 34 56 78" required data-testid="booking-phone-input" className="mt-2 bg-black/50 border-white/10 text-white placeholder:text-zinc-600 h-12 rounded-sm focus-visible:ring-[#CCFF00]" />
                  </div>
                  <div>
                    <Label className="text-[10px] uppercase tracking-[0.2em] text-zinc-500">Catégorie d&apos;âge</Label>
                    <Select value={ageCategory} onValueChange={setAgeCategory}>
                      <SelectTrigger className="mt-2 bg-black/50 border-white/10 text-white h-12 rounded-sm" data-testid="booking-age-select">
                        <SelectValue placeholder="Catégorie" />
                      </SelectTrigger>
                      <SelectContent className="bg-[#0a0a0a] border-white/10 text-white">
                        {AGES.map((a) => <SelectItem key={a} value={a}>{a}</SelectItem>)}
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="md:col-span-2">
                    <Label className="text-[10px] uppercase tracking-[0.2em] text-zinc-500">Niveau actuel</Label>
                    <Select value={level} onValueChange={setLevel}>
                      <SelectTrigger className="mt-2 bg-black/50 border-white/10 text-white h-12 rounded-sm" data-testid="booking-level-select">
                        <SelectValue placeholder="Niveau" />
                      </SelectTrigger>
                      <SelectContent className="bg-[#0a0a0a] border-white/10 text-white">
                        {LEVELS.map((l) => <SelectItem key={l} value={l}>{l}</SelectItem>)}
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="md:col-span-2">
                    <Label className="text-[10px] uppercase tracking-[0.2em] text-zinc-500">Message (optionnel)</Label>
                    <Textarea value={message} onChange={(e) => setMessage(e.target.value)} placeholder="Tes objectifs, ton club actuel..." rows={4} data-testid="booking-message-input" className="mt-2 bg-black/50 border-white/10 text-white placeholder:text-zinc-600 rounded-sm focus-visible:ring-[#CCFF00]" />
                  </div>
                  <button type="submit" disabled={submitting} data-testid="booking-submit" className="md:col-span-2 mt-2 inline-flex items-center justify-center gap-3 bg-[#CCFF00] text-black font-bold uppercase tracking-wide text-sm px-7 py-4 rounded-sm hover:bg-white transition-colors disabled:opacity-50">
                    {submitting ? "Envoi en cours…" : "Confirmer la réservation"}
                    <ArrowRight className="w-4 h-4" />
                  </button>
                </form>
              </div>
            </motion.div>
          )}

          {/* ============ PHASE: SUCCESS ============ */}
          {phase === "success" && (
            <motion.div
              key="phase-success"
              initial={{ opacity: 0, scale: 0.97 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ duration: 0.3 }}
              className="bg-[#0a0a0a] border border-[#CCFF00]/40 p-10 md:p-16 rounded-sm text-center max-w-2xl mx-auto"
              data-testid="booking-success"
            >
              <Stepper current={2} />
              <CheckCircle2 className="w-14 h-14 text-[#CCFF00] mx-auto" strokeWidth={1.5} />
              <h3 className="mt-6 font-display text-4xl md:text-5xl leading-none">
                CRÉNEAU RÉSERVÉ.
              </h3>
              <p className="mt-4 text-zinc-400">
                Avec{" "}
                <span className="text-[#CCFF00] font-bold">{selectedCoach?.firstName} {selectedCoach?.lastName}</span>
                {" "}le{" "}
                <span className="text-[#CCFF00] font-bold">
                  {selectedDate && format(selectedDate, "EEEE d MMMM yyyy", { locale: fr })}
                </span>{" "}
                à{" "}
                <span className="text-[#CCFF00] font-bold">{selectedSlot}</span>.
              </p>
              <p className="mt-3 text-zinc-500 text-sm max-w-md mx-auto">
                Nous avons bien reçu ta demande et te recontacterons très
                prochainement par email ou téléphone pour confirmer
                définitivement le créneau.
              </p>
              <button
                onClick={resetAll}
                data-testid="booking-new-request"
                className="mt-8 inline-flex items-center gap-2 text-sm font-bold uppercase tracking-wider text-[#CCFF00] hover:text-white transition-colors"
              >
                Faire une autre réservation <ArrowRight className="w-4 h-4" />
              </button>
            </motion.div>
          )}
        </AnimatePresence>
      </div>
    </section>
  );
}

// ===== Helpers =====
function TarifCard({ id, title, badge, description, bullets, onPick, accent }) {
  return (
    <motion.button
      type="button"
      onClick={() => onPick(id)}
      data-testid={`tarif-card-${id}`}
      initial={{ opacity: 0, y: 30 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.5, delay: accent ? 0.1 : 0 }}
      whileHover={{ y: -6 }}
      className={`relative text-left rounded-sm p-8 md:p-10 border bg-[#0a0a0a] hover:bg-[#0d0d0d] transition-all group ${
        accent
          ? "border-[#CCFF00] shadow-[0_0_60px_-20px_#CCFF00]"
          : "border-white/10 hover:border-[#CCFF00]/50"
      }`}
    >
      <div className="flex items-center justify-between mb-6">
        <span
          className={`text-[10px] uppercase tracking-[0.3em] ${
            accent ? "text-[#CCFF00]" : "text-zinc-500"
          }`}
        >
          {badge}
        </span>
        <ArrowRight
          className={`w-5 h-5 transition-transform group-hover:translate-x-1 ${
            accent ? "text-[#CCFF00]" : "text-zinc-500 group-hover:text-[#CCFF00]"
          }`}
        />
      </div>
      <h3 className="font-display text-4xl md:text-5xl leading-[0.9]">
        {title.split(" ")[0]}
        <br />
        <span className={accent ? "text-[#CCFF00]" : "text-white"}>
          {title.split(" ").slice(1).join(" ")}
        </span>
      </h3>
      <p className="mt-5 text-sm text-zinc-400 leading-relaxed">{description}</p>
      <ul className="mt-8 space-y-2.5">
        {bullets.map((b) => (
          <li key={b} className="flex items-start gap-3 text-sm text-zinc-300">
            <span
              className={`mt-1.5 w-1.5 h-1.5 rounded-full flex-shrink-0 ${
                accent ? "bg-[#CCFF00]" : "bg-zinc-600"
              }`}
            />
            {b}
          </li>
        ))}
      </ul>
      <span
        className={`mt-10 inline-flex items-center gap-2 text-xs font-bold uppercase tracking-wider ${
          accent ? "text-[#CCFF00]" : "text-white group-hover:text-[#CCFF00]"
        }`}
      >
        Choisir <ArrowRight className="w-4 h-4" />
      </span>
    </motion.button>
  );
}

function Stepper({ current }) {
  return (
    <div className="flex items-center gap-3 md:gap-6 mb-8 overflow-x-auto" data-testid="booking-stepper">
      {STEP_LABELS.map((label, i) => {
        const active = current === i;
        const done = current > i;
        return (
          <div key={label} className="flex items-center gap-3 flex-shrink-0">
            <div
              className={`w-8 h-8 grid place-items-center text-xs font-bold border transition-colors ${
                done
                  ? "bg-[#CCFF00] border-[#CCFF00] text-black"
                  : active
                  ? "border-[#CCFF00] text-[#CCFF00]"
                  : "border-white/15 text-zinc-600"
              }`}
            >
              {done ? "✓" : `0${i + 1}`}
            </div>
            <span className={`text-[11px] tracking-[0.2em] uppercase ${active ? "text-white" : "text-zinc-500"}`}>
              {label}
            </span>
            {i < STEP_LABELS.length - 1 && <span className="w-6 md:w-12 h-px bg-white/10" />}
          </div>
        );
      })}
    </div>
  );
}

function CoachBackdrop() {
  return (
    <>
      <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_center,rgba(204,255,0,0.15),transparent_55%)]" />
      <div className="absolute inset-0 bg-[linear-gradient(110deg,transparent_0%,transparent_40%,rgba(204,255,0,0.06)_50%,transparent_60%,transparent_100%)]" />
      <svg
        className="absolute inset-0 w-full h-full opacity-[0.06]"
        xmlns="http://www.w3.org/2000/svg"
      >
        <defs>
          <pattern id="grid" width="40" height="40" patternUnits="userSpaceOnUse">
            <path d="M 40 0 L 0 0 0 40" fill="none" stroke="white" strokeWidth="0.5" />
          </pattern>
        </defs>
        <rect width="100%" height="100%" fill="url(#grid)" />
      </svg>
    </>
  );
}

function CoachCard({ coach, index, onPick }) {
  const isLocked = !coach.available;
  return (
    <motion.button
      type="button"
      onClick={() => onPick(coach)}
      disabled={isLocked}
      data-testid={`coach-card-${coach.id}`}
      initial={{ opacity: 0, y: 40 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.6, delay: index * 0.15, ease: "easeOut" }}
      whileHover={
        isLocked
          ? {}
          : { y: -8, transition: { duration: 0.25 } }
      }
      className={`group relative bg-gradient-to-br from-[#0a0a0a] via-[#0a0a0a] to-[#050505] border rounded-sm overflow-hidden text-left min-h-[520px] md:min-h-[600px] ${
        isLocked
          ? "border-white/5 cursor-not-allowed"
          : "border-white/10 hover:border-[#CCFF00] cursor-pointer"
      }`}
    >
      <CoachBackdrop />

      {/* Top badge row */}
      <div className="absolute top-5 left-5 right-5 z-20 flex items-center justify-between">
        {isLocked ? (
          <div className="bg-black/60 backdrop-blur-sm border border-white/10 px-3 py-1.5 flex items-center gap-2">
            <Lock className="w-3 h-3 text-zinc-500" />
            <span className="text-[10px] uppercase tracking-[0.2em] text-zinc-400">
              Verrouillé
            </span>
          </div>
        ) : (
          <span />
        )}
        {coach.role ? (
          <div className="bg-black/60 backdrop-blur-sm border border-white/10 px-3 py-1.5">
            <p className="text-[10px] uppercase tracking-[0.2em] text-zinc-300">
              {coach.role}
            </p>
          </div>
        ) : (
          <span />
        )}
      </div>

      {/* Image or silhouette */}
      <div className="absolute inset-0 z-10 overflow-hidden">
        {!isLocked ? (
          <img
            src={coach.image}
            alt={coach.name}
            style={{
              transform: `translateY(${coach.imageOffsetY || "0%"}) scale(${coach.imageScale || 1})`,
              transformOrigin: `center ${coach.imageOriginY || "top"}`,
            }}
            className="absolute inset-0 w-full h-full object-cover object-top drop-shadow-[0_25px_30px_rgba(0,0,0,0.7)] transition-transform duration-500 group-hover:scale-[1.04]"
          />
        ) : (
          <div className="absolute inset-0 flex items-end justify-center">
            <LockedSilhouette />
          </div>
        )}
      </div>

      {/* Bottom name + CTA */}
      <div className="absolute bottom-0 left-0 right-0 p-6 z-20 bg-gradient-to-t from-black via-black/85 to-transparent">
        <p className="font-display text-4xl md:text-5xl leading-[0.9]">
          {coach.firstName}
          <br />
          <span className={isLocked ? "text-zinc-600" : "text-[#CCFF00]"}>
            {coach.lastName}
          </span>
        </p>
        {!isLocked ? (
          <div className="mt-4 flex items-center justify-end">
            <span className="inline-flex items-center gap-2 text-xs font-bold uppercase tracking-wider text-[#CCFF00] group-hover:text-white transition-colors">
              Choisir <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
            </span>
          </div>
        ) : (
          <div className="mt-4 flex items-center gap-2 text-[10px] uppercase tracking-[0.25em] text-zinc-500">
            <Sparkles className="w-3 h-3" /> Bientôt disponible
          </div>
        )}
      </div>

      {/* Glow border on hover */}
      {!isLocked && (
        <div className="pointer-events-none absolute inset-0 z-30 opacity-0 group-hover:opacity-100 transition-opacity duration-300 shadow-[inset_0_0_80px_-20px_#CCFF00]" />
      )}
    </motion.button>
  );
}

function LockedSilhouette() {
  return (
    <div className="relative h-[80%] w-full flex items-end justify-center">
      <svg
        viewBox="0 0 200 400"
        className="h-full w-auto opacity-[0.35]"
        fill="none"
        xmlns="http://www.w3.org/2000/svg"
      >
        <defs>
          <linearGradient id="silhouetteGrad" x1="0" y1="0" x2="0" y2="1">
            <stop offset="0%" stopColor="#1a1a1a" />
            <stop offset="100%" stopColor="#050505" />
          </linearGradient>
        </defs>
        {/* Head */}
        <circle cx="100" cy="60" r="32" fill="url(#silhouetteGrad)" stroke="#222" strokeWidth="1" />
        {/* Neck */}
        <rect x="92" y="88" width="16" height="14" fill="url(#silhouetteGrad)" />
        {/* Shoulders/torso (arms crossed pose) */}
        <path
          d="M 50 130 Q 60 110 100 105 Q 140 110 150 130 L 165 220 Q 165 240 155 250 L 130 260 L 130 320 L 70 320 L 70 260 L 45 250 Q 35 240 35 220 Z"
          fill="url(#silhouetteGrad)"
          stroke="#222"
          strokeWidth="1"
        />
        {/* Crossed arms */}
        <path d="M 55 160 L 145 160 L 140 200 L 60 200 Z" fill="#0d0d0d" stroke="#222" strokeWidth="1" />
      </svg>
      <Lock className="absolute top-1/2 -translate-y-1/2 w-12 h-12 text-[#CCFF00]/60" strokeWidth={1.5} />
    </div>
  );
}
